console.log('Checkout upsell modal script loaded');

/** Puntos SVG para “starburst” promocional (sol dentado). */
function burstPolygonPoints(cx, cy, outerR, innerR, teeth) {
  const pts = [];
  const step = (2 * Math.PI) / teeth;
  for (let i = 0; i < teeth; i++) {
    const aOut = -Math.PI / 2 + i * step;
    const aIn = -Math.PI / 2 + i * step + step / 2;
    pts.push(`${cx + outerR * Math.cos(aOut)},${cy + outerR * Math.sin(aOut)}`);
    pts.push(`${cx + innerR * Math.cos(aIn)},${cy + innerR * Math.sin(aIn)}`);
  }
  return pts.join(' ');
}

if (!customElements.get('checkout-upsell-modal')) {
  customElements.define(
    'checkout-upsell-modal',
    class CheckoutUpsellModal extends HTMLElement {
      constructor() {
        super();
        this.productsContainer = this.querySelector('#CheckoutUpsellModal-Products');
        this.overlay = this.querySelector('[data-modal-overlay]');
        this.closeButtons = this.querySelectorAll('[data-modal-close]');
        this.cartItemIds = new Set();
        this.products = [];
        this.isOpen = false;
        this.initialized = false;
        /** Ya programamos la apertura automática en esta carga de página */
        this._cartModalAutoOpenScheduled = false;
        /** Intentos esperando window.routes (theme script) */
        this._routesWaitAttempts = 0;
        
        // Get configuration from JSON script tag
        this.config = {
          modalTitle: 'Want to add these products to your order?',
          defaultCollections: [],
          rules: []
        };
        
        try {
          const configScript = document.getElementById('CheckoutUpsellModal-Config');
          if (configScript) {
            this.config = JSON.parse(configScript.textContent);
            console.log('📋 Modal configuration loaded:', this.config);
          }
        } catch (e) {
          console.error('Error parsing modal configuration:', e);
        }
        
        this.modalTitle = this.config.modalTitle;
        this.rules = this.config.rules || [];
        this.defaultCollections = this.config.defaultCollections || [];
        this.upgradeRules = this.config.upgradeRules || [];
        
        // Update title if custom
        const titleElement = this.querySelector('#CheckoutUpsellModal-Title');
        if (titleElement && this.modalTitle) {
          titleElement.textContent = this.modalTitle;
        }
      }

      connectedCallback() {
        console.log('Checkout upsell modal connected to DOM');
        if (!this.initialized) {
          this.init();
          this.initialized = true;
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      init() {
        // Set up close handlers
        this.closeButtons.forEach((button) => {
          button.addEventListener('click', (e) => {
            const shouldProceedToCheckout = button.classList.contains('checkout-upsell-modal__button--secondary');
            this.close(shouldProceedToCheckout);
          });
        });

        if (this.overlay) {
          this.overlay.addEventListener('click', () => this.close(false));
        }

        // Listen for cart updates to refresh cart item IDs
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          this.updateCartItemIds();
        });

        // Wait for DOM to be ready, then check and show modal
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            this.initializeModal();
          });
        } else {
          this.initializeModal();
        }

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.isOpen) {
            this.close();
          }
        });
      }

      async initializeModal() {
        if (!this.isConnected) {
          return;
        }

        if (!this.productsContainer) {
          this.productsContainer = this.querySelector('#CheckoutUpsellModal-Products');
        }
        if (!this.productsContainer) {
          return;
        }

        if (!window.routes || !window.routes.cart_url) {
          this._routesWaitAttempts += 1;
          if (this._routesWaitAttempts > 40) {
            console.warn('checkout-upsell-modal: window.routes still unavailable after retries');
            return;
          }
          setTimeout(() => this.initializeModal(), 150);
          return;
        }

        this._routesWaitAttempts = 0;

        await this.updateCartItemIds();

        // Varios intentos: DOM/carrito a veces aún no están listos en la primera pasada
        this.scheduleCartModalAttempts();
      }

      /**
       * Detecta la página del carrito con o sin prefijo de idioma (/cart, /es/cart, …)
       */
      isCartPage() {
        const pathname = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
        const segments = pathname.split('/').filter(Boolean);
        const lastSeg = segments[segments.length - 1];

        if (pathname === '/cart' || pathname.endsWith('/cart') || lastSeg === 'cart') {
          return true;
        }

        if (
          document.querySelector(
            'cart-items, cart-page, cart-notification, #cart, #CartPage, #MainCart, #main-cart-items, #MainContent cart-items, .cart__contents, .cart__warnings, [data-section-type="cart"], form[action*="/cart"]'
          )
        ) {
          return true;
        }

        const r = window.routes;
        if (r && r.cart_url) {
          try {
            const cartPath = new URL(r.cart_url, window.location.origin).pathname.replace(/\/+$/, '') || '/';
            if (pathname === cartPath || pathname.endsWith(cartPath)) {
              return true;
            }
          } catch (e) {
            const rel = String(r.cart_url).split('?')[0].replace(/\/+$/, '') || '/';
            if (rel.startsWith('/') && (pathname === rel || pathname.endsWith(rel))) {
              return true;
            }
          }
        }

        return false;
      }

      /**
       * Programa varios intentos de apertura para evitar condiciones de carrera.
       */
      scheduleCartModalAttempts() {
        if (sessionStorage.getItem('checkout-upsell-upgrade-done')) {
          return;
        }

        const delays = [120, 450, 1100, 2400, 4000];
        delays.forEach((ms) => {
          setTimeout(() => this.tryShowCartModal(), ms);
        });
      }

      /**
       * Abre el modal si corresponde (una vez por sesión de pestaña salvo testing manual).
       */
      async tryShowCartModal() {
        if (sessionStorage.getItem('checkout-upsell-upgrade-done')) {
          return;
        }

        if (this.isOpen || this._cartModalAutoOpenScheduled) {
          return;
        }

        if (!this.isCartPage()) {
          return;
        }

        if (!window.routes || !routes.cart_url) {
          return;
        }

        if (!this.isConnected) {
          return;
        }

        if (!this.productsContainer) {
          this.productsContainer = this.querySelector('#CheckoutUpsellModal-Products');
          if (!this.productsContainer) {
            return;
          }
        }

        try {
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          if (!cartResponse.ok) {
            return;
          }

          const cart = await cartResponse.json();

          if (!cart || !cart.item_count || cart.item_count < 1) {
            return;
          }

          this._cartModalAutoOpenScheduled = true;

          const openDelayMs = 280;
          setTimeout(() => {
            this.open().catch((error) => {
              console.error('checkout-upsell-modal: error opening modal', error);
              this._cartModalAutoOpenScheduled = false;
              this.isOpen = false;
            });
          }, openDelayMs);
        } catch (error) {
          console.error('checkout-upsell-modal: cart check failed', error);
        }
      }


      async updateCartItemIds() {
        try {
          if (!routes || !routes.cart_url) {
            throw new Error('Routes not available');
          }
          
          // Get current cart items
          const cartUrl = `${routes.cart_url}.js`;
          const response = await fetch(cartUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch cart: ${response.status}`);
          }
          const cart = await response.json();
          this.cartItemIds = new Set(
            cart.items?.map((item) => item.product_id.toString()) || []
          );
          console.log('Cart item IDs updated:', Array.from(this.cartItemIds));
        } catch (error) {
          console.error('Error updating cart item IDs:', error);
          // Fallback: try to get from DOM
          const cartItems = document.querySelectorAll('[data-product-id]');
          this.cartItemIds = new Set(
            Array.from(cartItems).map((item) => item.dataset.productId)
          );
          if (this.cartItemIds.size > 0) {
            console.log('Cart item IDs from DOM:', Array.from(this.cartItemIds));
          }
        }
      }

      async open() {
        console.log('🔓 Opening modal...');
        
        if (!this.isConnected) {
          const err = new Error('Modal not connected to DOM');
          console.error('❌ Cannot open modal:', err);
          throw err;
        }
        
        // Check if element exists
        if (!this.productsContainer) {
          console.error('❌ Products container not found, re-querying...');
          this.productsContainer = this.querySelector('#CheckoutUpsellModal-Products');
          if (!this.productsContainer) {
            const err = new Error('Products container #CheckoutUpsellModal-Products missing');
            console.error('❌ Products container still not found after re-query');
            console.log('Available elements in modal:', Array.from(this.children).map(c => c.tagName));
            throw err;
          }
        }
        
        console.log('✅ Modal element found, making visible...');
        
        this.isOpen = true;
        // Remove hidden attribute and ensure it's visible
        this.removeAttribute('hidden');
        this.style.display = 'flex';
        this.style.visibility = 'visible';
        this.style.opacity = '1';
        this.style.position = 'fixed';
        this.style.top = '0';
        this.style.left = '0';
        this.style.width = '100%';
        this.style.height = '100%';
        this.style.zIndex = '9999';
        document.body.classList.add('overflow-hidden', 'checkout-upsell-modal-is-open');
        
        // Force a reflow to ensure styles apply
        void this.offsetHeight;
        
        // Check computed styles after a brief delay
        setTimeout(() => {
          const computed = window.getComputedStyle(this);
          console.log('📊 Modal element state after opening:', {
            hidden: this.hasAttribute('hidden'),
            isOpen: this.isOpen,
            display: computed.display,
            visibility: computed.visibility,
            opacity: computed.opacity,
            zIndex: computed.zIndex,
            position: computed.position,
            width: computed.width,
            height: computed.height
          });
          
          // Check if dialog is visible
          const dialog = this.querySelector('.checkout-upsell-modal__dialog');
          if (dialog) {
            const dialogComputed = window.getComputedStyle(dialog);
            console.log('📊 Dialog element state:', {
              display: dialogComputed.display,
              visibility: dialogComputed.visibility,
              opacity: dialogComputed.opacity
            });
          } else {
            console.error('❌ Dialog element not found!');
          }
        }, 100);

        // Load products
        console.log('📦 Loading products...');
        await this.loadProducts();
        console.log('✅ Products loaded');
      }

      close(proceedToCheckout = false) {
        this.isOpen = false;
        this.setAttribute('hidden', '');
        this.style.display = 'none';
        this.style.visibility = 'hidden';
        this.style.opacity = '0';
        document.body.classList.remove('overflow-hidden', 'checkout-upsell-modal-is-open');

        // If user clicked "No, go to checkout", redirect to checkout
        if (proceedToCheckout) {
          window.location.href = '/checkout';
        }
      }

      findMatchingUpgradeRule(cart) {
        if (!this.upgradeRules || this.upgradeRules.length === 0) return null;

        for (const rule of this.upgradeRules) {
          if (!rule.triggerHandle) continue;
          const triggerItem = cart.items.find(item => item.handle === rule.triggerHandle);
          if (triggerItem) {
            return { rule, triggerItem };
          }
        }
        return null;
      }

      renderUpgradeOffer(rule) {
        this.classList.add('checkout-upsell-modal--upgrade');

        const headerEl = this.querySelector('.checkout-upsell-modal__header');
        if (headerEl) {
          headerEl.innerHTML = `
            <div class="upgrade-header-label">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3a1 1 0 01-1 1H6a1 1 0 01-1-1v-1h14v1z"/></svg>
              ${this.escapeHtml(rule.headerLabel || 'BUNDLE EXCLUSIVO')}
            </div>
            <button type="button" class="checkout-upsell-modal__close" aria-label="Close" data-modal-close>
              <span class="svg-wrapper"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14"><path d="M13.71.29a1 1 0 00-1.42 0L7 5.59 1.71.29A1 1 0 00.29 1.71L5.59 7 .29 12.29a1 1 0 101.42 1.42L7 8.41l5.29 5.3a1 1 0 001.42-1.42L8.41 7l5.3-5.29a1 1 0 000-1.42z" fill="currentColor"/></svg></span>
            </button>
          `;
          headerEl.querySelector('[data-modal-close]').addEventListener('click', () => this.close(false));
        }

        const items = rule.items || [];
        const itemsHtml = items.map(item => {
          const priceText =
            item.price != null && String(item.price).trim() !== ''
              ? String(item.price).trim()
              : '';
          return `
          <div class="checkout-upsell-upgrade__item">
            ${item.image ? `<img class="checkout-upsell-upgrade__item-image" src="${item.image}" alt="" loading="lazy">` : ''}
            <div class="checkout-upsell-upgrade__item-info">
              <p class="checkout-upsell-upgrade__item-title">${this.escapeHtml(item.title)}</p>
              ${item.description ? `<p class="checkout-upsell-upgrade__item-desc">${this.escapeHtml(item.description)}</p>` : ''}
            </div>
            ${priceText ? `<span class="checkout-upsell-upgrade__item-price">${this.escapeHtml(priceText)}</span>` : ''}
          </div>
        `;
        }).join('');

        this.productsContainer.innerHTML = `
          <div class="checkout-upsell-upgrade">
            <div class="checkout-upsell-upgrade__headline">
              <h2 class="checkout-upsell-upgrade__title">${this.escapeHtml(rule.offerTitle || '')}</h2>
              ${rule.bundlePrice ? `
              <span class="checkout-upsell-upgrade__headline-burst" aria-hidden="true">
                <svg class="checkout-upsell-upgrade__headline-burst-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <polygon fill="#e30613" stroke="#ffffff" stroke-width="2.25" stroke-linejoin="round" points="${burstPolygonPoints(50, 50, 49, 37, 18)}"/>
                </svg>
                <span class="checkout-upsell-upgrade__headline-burst-text">${this.escapeHtml(rule.bundlePrice)}</span>
              </span>` : ''}
            </div>
            ${rule.offerSubtitle ? `<p class="checkout-upsell-upgrade__subtitle">${this.escapeHtml(rule.offerSubtitle)}</p>` : ''}
            <div class="checkout-upsell-upgrade__items">${itemsHtml}</div>
            ${rule.bundlePrice || rule.pricingNote ? `
              <div class="checkout-upsell-upgrade__pricing-block">
                ${rule.bundlePrice ? `
                <div class="checkout-upsell-upgrade__pricing">
                  <div class="checkout-upsell-upgrade__pricing-left">
                    <span class="checkout-upsell-upgrade__pricing-label">${this.escapeHtml(rule.pricingLabel || 'Bundle:')}</span>
                    <span class="checkout-upsell-upgrade__pricing-bundle">${this.escapeHtml(rule.bundlePrice)}</span>
                    ${rule.originalPrice ? `<span class="checkout-upsell-upgrade__pricing-original">${this.escapeHtml(rule.originalPrice)}</span>` : ''}
                  </div>
                  ${rule.discountBadge ? `<span class="checkout-upsell-upgrade__pricing-badge">${this.escapeHtml(rule.discountBadge)}</span>` : ''}
                </div>
                ` : ''}
                ${rule.pricingNote ? `<p class="checkout-upsell-upgrade__pricing-note">${this.escapeHtml(rule.pricingNote)}</p>` : ''}
              </div>
            ` : ''}
            <button type="button" class="checkout-upsell-upgrade__cta" data-upgrade-btn>
              ${this.escapeHtml(rule.buttonText || 'Añadir el bundle a mi orden')}
            </button>
            <button type="button" class="checkout-upsell-upgrade__skip">
              ${this.escapeHtml(rule.skipText || 'Continuar sin el merch')}
            </button>
          </div>
        `;

        this.productsContainer.querySelector('[data-upgrade-btn]').addEventListener('click', () =>
          this.handleUpgrade(rule, this.productsContainer.querySelector('[data-upgrade-btn]'))
        );
        this.productsContainer.querySelector('.checkout-upsell-upgrade__skip').addEventListener('click', () => this.close(false));
      }

      async handleUpgrade(rule, btn) {
        btn.disabled = true;
        btn.classList.add('loading');
        const originalText = btn.textContent;
        btn.textContent = '';

        try {
          if (!rule.replacementHandle) throw new Error('No product handle configured');

          const addRes = await fetch(`/products/${rule.replacementHandle}.js`);
          if (!addRes.ok) throw new Error('Product not found');
          const addProduct = await addRes.json();

          const variantToAdd = addProduct.variants.find(v => v.available) || addProduct.variants[0];
          if (!variantToAdd) throw new Error('No available variant');

          const addCartRes = await fetch(`${routes.cart_add_url}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantToAdd.id, quantity: 1 }),
          });

          if (!addCartRes.ok) {
            const errBody = await addCartRes.text().catch(() => '');
            throw new Error(errBody || `Cart add failed (${addCartRes.status})`);
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'checkout-upsell-modal' });

          sessionStorage.setItem('checkout-upsell-upgrade-done', 'true');

          btn.classList.remove('loading');
          btn.textContent = '¡Listo!';
          btn.style.background = '#4caf50';

          setTimeout(() => this.close(false), 1200);
          setTimeout(() => window.location.reload(), 1300);
        } catch (error) {
          console.error('Upgrade error:', error);
          btn.disabled = false;
          btn.classList.remove('loading');
          btn.textContent = originalText;
          alert('Hubo un error. Intenta de nuevo.');
        }
      }

      async loadProducts() {
        this.productsContainer.innerHTML = `
          <div class="checkout-upsell-modal__loading">
            <div class="loading__spinner"></div>
            <p>Loading products...</p>
          </div>
        `;

        try {
          const cartRes = await fetch(`${routes.cart_url}.js`);
          const cart = await cartRes.json();
          const upgradeMatch = this.findMatchingUpgradeRule(cart);
          if (upgradeMatch) {
            this.renderUpgradeOffer(upgradeMatch.rule);
            return;
          }

          // Check if any rules match
          const matchingRule = this.findMatchingRule();
          let productsToShow = [];
          
          if (matchingRule) {
            console.log('✅ Found matching rule:', matchingRule);
            // Load recommended products from the matching rule
            productsToShow = await this.loadProductsByIds(matchingRule.recommended_products || []);
          } else if (this.defaultCollections && this.defaultCollections.length > 0) {
            console.log('📋 Using default collections:', this.defaultCollections);
            // Use products from default collections
            productsToShow = await this.loadProductsFromCollections(this.defaultCollections);
          } else {
            // No matching rule and no default products, load all products
            console.log('🌐 Loading all products (no rules or defaults)');
            const response = await fetch('/collections/all/products.json?limit=250');
            if (!response.ok) {
              throw new Error('Failed to fetch products');
            }
            const data = await response.json();
            this.products = data.products || [];
            
            // Filter out products already in cart
            productsToShow = this.products.filter(
              (product) => !this.cartItemIds.has(product.id.toString())
            );
          }
          
          console.log(`✅ Showing ${productsToShow.length} products`);
          
          // Show products
          this.renderProducts(productsToShow);
        } catch (error) {
          console.error('Error loading products:', error);
          this.productsContainer.innerHTML = `
            <div class="checkout-upsell-modal__empty">
              <p>Unable to load products at this time.</p>
            </div>
          `;
        }
      }

      findMatchingRule() {
        // Check each rule to see if cart contains any trigger products
        for (const rule of this.rules) {
          if (!rule.trigger_products || rule.trigger_products.length === 0) {
            continue;
          }
          
          // Check if any trigger product is in cart
          const hasTriggerProduct = rule.trigger_products.some(productId => {
            return this.cartItemIds.has(productId.toString());
          });
          
          if (hasTriggerProduct) {
            return rule;
          }
        }
        
        return null;
      }

      async loadProductsByIds(productIds) {
        if (!productIds || productIds.length === 0) {
          return [];
        }
        
        const products = [];
        
        // Fetch products by ID (Shopify doesn't have a direct API for this, so we'll fetch all and filter)
        const response = await fetch('/collections/all/products.json?limit=250');
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        
        const data = await response.json();
        const allProducts = data.products || [];
        
        // Filter to only include products in our list and not in cart
        const productIdSet = new Set(productIds.map(id => id.toString()));
        const filteredProducts = allProducts.filter(product => {
          return productIdSet.has(product.id.toString()) && 
                 !this.cartItemIds.has(product.id.toString());
        });
        
        return filteredProducts;
      }

      async loadProductsFromCollections(collectionHandles) {
        if (!collectionHandles || collectionHandles.length === 0) {
          return [];
        }
        
        const allProducts = [];
        const seenProductIds = new Set();
        
        // Fetch products from each collection using collection handles
        for (const collectionHandle of collectionHandles) {
          try {
            const response = await fetch(`/collections/${collectionHandle}/products.json?limit=250`);
            if (!response.ok) {
              console.warn(`Failed to fetch collection ${collectionHandle}:`, response.status);
              continue;
            }
            
            const data = await response.json();
            const products = data.products || [];
            
            // Add products that aren't already seen and aren't in cart
            for (const product of products) {
              const productId = product.id.toString();
              if (!seenProductIds.has(productId) && !this.cartItemIds.has(productId)) {
                seenProductIds.add(productId);
                allProducts.push(product);
              }
            }
          } catch (error) {
            console.error(`Error fetching collection ${collectionHandle}:`, error);
          }
        }
        
        return allProducts;
      }

      findLowestPriceVariant(variants) {
        if (!variants || variants.length === 0) {
          return null;
        }

        let lowestVariant = null;
        let lowestPrice = Infinity;

        for (const variant of variants) {
          if (!variant.price) continue;

          // Parse price - handle both dollars (with decimal) and cents (without decimal)
          const priceStr = String(variant.price);
          let priceValue = parseFloat(priceStr);

          // If price doesn't have decimal and is > 1000, it's likely in cents
          if (!priceStr.includes('.') && priceValue >= 1000) {
            priceValue = priceValue / 100; // Convert cents to dollars
          }

          // Only consider available variants
          if (variant.available !== false && priceValue < lowestPrice) {
            lowestPrice = priceValue;
            lowestVariant = variant;
          }
        }

        return lowestVariant;
      }

      renderProducts(products) {
        if (products.length === 0) {
          this.productsContainer.innerHTML = `
            <div class="checkout-upsell-modal__empty">
              <p>No additional products available.</p>
            </div>
          `;
          return;
        }

        this.productsContainer.innerHTML = products
          .map(
            (product) => {
              const imageUrl = this.getProductImage(product);
              
              // Find the variant with the lowest price
              const lowestPriceVariant = this.findLowestPriceVariant(product.variants);
              
              // Try multiple price sources - Shopify products.json API structure
              let rawPrice = null;
              let priceAlreadyInDollars = false;
              
              // Use the lowest price variant if available
              if (lowestPriceVariant) {
                if (lowestPriceVariant.price) {
                  const variantPriceStr = String(lowestPriceVariant.price);
                  // If it's a string with decimal point or less than 1000, it's likely already in dollars
                  if (variantPriceStr.includes('.') || parseFloat(variantPriceStr) < 1000) {
                    rawPrice = parseFloat(variantPriceStr);
                    priceAlreadyInDollars = true;
                  } else {
                    rawPrice = parseFloat(variantPriceStr);
                  }
                }
              }
              
              // Fallback to product-level pricing if variant price not available
              if (!rawPrice) {
                if (product.price_min) {
                  rawPrice = product.price_min;
                } else if (product.price) {
                  rawPrice = product.price;
                }
              }
              
              const price = rawPrice 
                ? (priceAlreadyInDollars 
                    ? this.formatPriceFromDollars(rawPrice) 
                    : this.formatPrice(rawPrice))
                : 'Price unavailable';
              const variantId = lowestPriceVariant ? lowestPriceVariant.id : null;
              const isAvailable = lowestPriceVariant && lowestPriceVariant.available !== false;
              
              console.log(`Product ${product.id} (${product.title}):`, {
                imageUrl,
                variantId,
                productPriceMin: product.price_min,
                productPriceMax: product.price_max,
                productPrice: product.price,
                variantPrice: lowestPriceVariant?.price,
                variantCompareAtPrice: lowestPriceVariant?.compare_at_price,
                selectedRawPrice: rawPrice,
                priceAlreadyInDollars: priceAlreadyInDollars,
                formattedPrice: price,
                available: isAvailable,
                allVariantPrices: product.variants?.map(v => ({ id: v.id, price: v.price, compare_at_price: v.compare_at_price }))
              });
              
              return `
          <div class="checkout-upsell-modal__product" data-product-id="${product.id}">
            <div class="checkout-upsell-modal__product-image-wrapper">
              ${imageUrl ? `
              <img
                class="checkout-upsell-modal__product-image"
                src="${imageUrl}"
                alt="${this.escapeHtml(product.title)}"
                loading="lazy"
                onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
              />
              <div style="display:none; align-items:center; justify-content:center; width:100%; height:100%; background:#f0f0f0; color:#999;">
                <span>No Image</span>
              </div>
              ` : `
              <div style="display:flex; align-items:center; justify-content:center; width:100%; height:100%; background:#f0f0f0; color:#999;">
                <span>No Image</span>
              </div>
              `}
            </div>
            <div class="checkout-upsell-modal__product-info">
              <h3 class="checkout-upsell-modal__product-title">${this.escapeHtml(product.title)}</h3>
              <div class="checkout-upsell-modal__product-price">
                ${price}
              </div>
              <button
                class="checkout-upsell-modal__product-button"
                data-variant-id="${variantId}"
                data-product-id="${product.id}"
                ${!isAvailable || !variantId ? 'disabled' : ''}
              >
                ${!isAvailable ? 'Sold Out' : !variantId ? 'Unavailable' : 'Add to Cart'}
              </button>
            </div>
          </div>
        `;
            }
          )
          .join('');

        // Attach add to cart handlers
        this.attachAddToCartHandlers();
      }

      attachAddToCartHandlers() {
        const addToCartButtons = this.productsContainer.querySelectorAll(
          '.checkout-upsell-modal__product-button'
        );

        addToCartButtons.forEach((button) => {
          button.addEventListener('click', async (e) => {
            e.preventDefault();
            e.stopPropagation();

            const variantId = button.dataset.variantId;
            const productId = button.dataset.productId;

            if (!variantId || button.disabled) {
              console.error('No variant ID found or button disabled');
              return;
            }

            // Disable button and show loading
            button.disabled = true;
            button.classList.add('loading');
            const originalText = button.textContent;
            button.textContent = 'Adding...';

            try {
              // Add to cart
              const response = await fetch(`${routes.cart_add_url}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  id: variantId,
                  quantity: 1,
                }),
              });

              let cartData;
              try {
                cartData = await response.json();
              } catch (parseError) {
                console.error('Failed to parse cart response:', parseError);
                // If parsing fails but response is ok, try to proceed
                if (response.ok) {
                  console.warn('Response OK but JSON parse failed, assuming success');
                  // Still trigger cart update to refresh cart
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'checkout-upsell-modal',
                    cartData: null,
                  });
                  // Update button state
                  button.classList.remove('loading');
                  button.textContent = 'Added!';
                  button.style.background = '#4caf50';
                  button.disabled = true;
                  return;
                }
                throw new Error('Failed to parse response from server');
              }

              // Check if response indicates an error (Shopify API returns status field on error)
              // But also check if we have valid cart data (item_count or items array) which indicates success
              const hasCartData = cartData && (cartData.item_count !== undefined || Array.isArray(cartData.items));
              
              if (cartData.status && !hasCartData) {
                // Only treat as error if status exists AND we don't have valid cart data
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'checkout-upsell-modal',
                  productVariantId: variantId,
                  errors: cartData.errors || cartData.description,
                  message: cartData.message,
                });
                throw new Error(cartData.description || cartData.message || 'Failed to add product to cart');
              }

              // If we have cart data, treat as success even if status field exists
              if (!hasCartData && !response.ok) {
                throw new Error('Failed to add product to cart');
              }

              // Update cart item IDs
              this.cartItemIds.add(productId);

              // Trigger cart update event
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'checkout-upsell-modal',
                cartData: cartData,
              });

              // Update button state
              button.classList.remove('loading');
              button.textContent = 'Added!';
              button.style.background = '#4caf50';
              button.disabled = true;

              // Remove product from view after a delay
              setTimeout(() => {
                const productElement = button.closest('.checkout-upsell-modal__product');
                if (productElement) {
                  productElement.style.opacity = '0';
                  productElement.style.transform = 'scale(0.9)';
                  productElement.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

                  setTimeout(() => {
                    productElement.remove();
                    // Check if no products left
                    const remainingProducts = this.productsContainer.querySelectorAll(
                      '.checkout-upsell-modal__product'
                    );
                    if (remainingProducts.length === 0) {
                      this.productsContainer.innerHTML = `
                        <div class="checkout-upsell-modal__empty">
                          <p>All available products have been added!</p>
                        </div>
                      `;
                    }
                  }, 300);
                }
              }, 1000);
            } catch (error) {
              console.error('Error adding to cart:', error);
              button.disabled = false;
              button.classList.remove('loading');
              button.textContent = originalText;
              alert('Unable to add product to cart. Please try again.');
            }
          });
        });
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      }

      getProductImage(product) {
        // Shopify products.json API returns images as string URLs
        // Check for featured_image first
        if (product.featured_image) {
          if (typeof product.featured_image === 'string') {
            // Ensure it's a full URL
            if (product.featured_image.startsWith('http')) {
              return product.featured_image;
            }
            // If relative, make it absolute
            if (product.featured_image.startsWith('//')) {
              return `https:${product.featured_image}`;
            }
            return product.featured_image;
          }
          // If it's an object, try to get src
          if (product.featured_image.src) {
            return product.featured_image.src;
          }
        }
        
        // Check images array (usually array of string URLs)
        if (product.images && product.images.length > 0) {
          const firstImage = product.images[0];
          if (typeof firstImage === 'string') {
            if (firstImage.startsWith('http')) {
              return firstImage;
            }
            if (firstImage.startsWith('//')) {
              return `https:${firstImage}`;
            }
            return firstImage;
          }
          // If it's an object
          if (firstImage.src) {
            return firstImage.src;
          }
          if (firstImage.url) {
            return firstImage.url;
          }
        }
        
        return '';
      }

      formatPrice(price) {
        if (price === null || price === undefined || price === '') return '';
        
        // Convert to number if it's a string
        let priceNum = typeof price === 'string' ? parseFloat(price) : price;
        
        if (isNaN(priceNum)) {
          console.error('Invalid price:', price);
          return '';
        }
        
        // Shopify products.json API returns prices in cents (e.g., 799 for $7.99, 6499 for $64.99)
        // Always treat as cents and divide by 100
        const amount = priceNum / 100;
        
        // Debug log for prices that seem suspiciously low
        if (amount < 1) {
          console.warn('⚠️ Low price detected:', {
            raw: priceNum,
            formatted: amount,
            'Possible issue': 'Price might be in wrong format or wrong field'
          });
        }
        
        // Try to get currency from Shopify settings or default to USD
        const currency = window.Shopify?.currency?.active || 'USD';
        const formatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
        
        return formatted;
      }

      formatPriceFromDollars(price) {
        if (price === null || price === undefined || price === '') return '';
        
        // Convert to number if it's a string
        let priceNum = typeof price === 'string' ? parseFloat(price) : price;
        
        if (isNaN(priceNum)) {
          console.error('Invalid price:', price);
          return '';
        }
        
        // Price is already in dollars, format directly
        const currency = window.Shopify?.currency?.active || 'USD';
        const formatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(priceNum);
        
        return formatted;
      }
    }
  );
}

// For testing: manually trigger the modal
// Usage in browser console: window.showCheckoutUpsellModal()
window.showCheckoutUpsellModal = function() {
  const modal = document.querySelector('checkout-upsell-modal');
  if (modal) {
    console.log('🔧 Manually triggering modal...');
    modal._cartModalAutoOpenScheduled = false;
    
    // Force show immediately
    modal.removeAttribute('hidden');
    modal.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      z-index: 99999 !important;
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    `;
    document.body.classList.add('overflow-hidden', 'checkout-upsell-modal-is-open');
    
    modal.open().then(() => {
      console.log('✅ Modal should now be visible');
      console.log('Check the page - you should see the modal overlay');
    }).catch((error) => {
      console.error('❌ Error opening modal:', error);
    });
  } else {
    console.error('❌ Checkout upsell modal not found in DOM');
    console.log('Looking for modal element...');
    const allElements = document.querySelectorAll('*');
    console.log(`Total elements: ${allElements.length}`);
    const customElements = Array.from(allElements).filter(el => el.tagName.includes('-'));
    console.log('Custom elements found:', customElements.map(el => el.tagName));
  }
};

// Also expose a function to check modal state
window.checkModalState = function() {
  const modal = document.querySelector('checkout-upsell-modal');
  if (!modal) {
    console.log('❌ Modal element not found in DOM');
    console.log('Checking if script loaded...');
    console.log('Custom elements registered:', customElements.get('checkout-upsell-modal') ? 'Yes' : 'No');
    return;
  }
  
  const computed = window.getComputedStyle(modal);
  const dialog = modal.querySelector('.checkout-upsell-modal__dialog');
  const dialogComputed = dialog ? window.getComputedStyle(dialog) : null;
  
  console.log('📊 Modal state:', {
    exists: !!modal,
    inDOM: modal.isConnected,
    hidden: modal.hasAttribute('hidden'),
    isOpen: modal.isOpen,
    display: computed.display,
    visibility: computed.visibility,
    opacity: computed.opacity,
    zIndex: computed.zIndex,
    position: computed.position,
    width: computed.width,
    height: computed.height,
    productsContainer: !!modal.querySelector('#CheckoutUpsellModal-Products'),
    dialogExists: !!dialog,
    dialogDisplay: dialogComputed?.display,
    dialogVisibility: dialogComputed?.visibility,
    dialogOpacity: dialogComputed?.opacity
  });
  
  // Also check if it's visible on screen
  const rect = modal.getBoundingClientRect();
  console.log('📐 Modal position:', {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    visible: rect.width > 0 && rect.height > 0
  });
};
